/* CommunityPage (iter-35 + iter55z scope split) — The Loom's in-app forum.
 *
 * Two forums:
 *   - "Community"      — open to all signed-in users
 *   - "House Writers"  — Pro Author + Premier Studio OR published in Bookstore
 *
 * Routes: /community            → list of topics (scoped)
 *         /community/:slug      → threads in a topic
 *         /community/t/:tid     → a single thread + replies
 *
 * We don't add router dependencies — three "views" are driven off a
 * component-state machine so this lands as ONE file.
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, PenLine, BookOpen, MessageSquare, LifeBuoy, ArrowLeft, Plus,
  Pin, Lock, Loader2, Send, Trash2, Coffee, Handshake, Crown, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { forumApi } from '@/lib/api';
import { useAuth } from '@/lib/auth_context';

const ICONS = { Sparkles, PenLine, BookOpen, MessageSquare, LifeBuoy, Coffee, Handshake };

const SCOPE_META = {
  community: { label: 'Community',      icon: Users, blurb: 'Open to every writer signed in.' },
  pro_house: { label: 'House Writers',  icon: Crown, blurb: 'Pro Author, Premier Studio, or published in the Bookstore.' },
};

function relTime(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, (Date.now() - t) / 1000);
    if (sec < 60)        return 'just now';
    if (sec < 3600)      return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400)     return `${Math.floor(sec / 3600)}h ago`;
    if (sec < 86400*30) return `${Math.floor(sec / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
}

function TopicList({ scope, onPick }) {
  const [data, setData] = useState({ topics: [], my_scopes: ['community'] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    forumApi.topics()
      .then(d => setData({ topics: d.topics || [], my_scopes: d.my_scopes || ['community'] }))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load forum.'))
      .finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(
    () => data.topics.filter((t) => (t.scope || 'community') === scope),
    [data.topics, scope],
  );
  const scopeAccessible = data.my_scopes.includes(scope);
  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading topics…</div>;
  }
  if (!scopeAccessible) {
    return (
      <div className="rounded-md border border-dashed border-amber-400 bg-amber-50/40 p-6 text-center" data-testid="forum-scope-locked">
        <Crown className="mx-auto h-6 w-6 text-amber-700" />
        <div className="mt-2 text-[14px] font-semibold">House Writers Lounge is a members-only room.</div>
        <p className="mx-auto mt-1 max-w-md text-[12px] text-muted-foreground">
          Available on <b>Pro Author</b> or <b>Premier Studio</b>, or automatically to any writer with at least one book published in the Bookstore. Upgrade in Settings → Subscription.
        </p>
      </div>
    );
  }
  if (filtered.length === 0) {
    return <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">No topics yet in this forum.</div>;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid={`forum-topic-list-${scope}`}>
      {filtered.map((t) => {
        const Icon = ICONS[t.icon] || MessageSquare;
        return (
          <button key={t.slug} type="button"
                   onClick={() => onPick(t.slug)}
                   data-testid={`forum-topic-${t.slug}`}
                   className="text-left rounded-md border border-border bg-card p-4 shadow-panel transition hover:border-primary hover:bg-primary/5">
            <div className="flex items-center gap-2 text-[14px] font-semibold">
              <Icon className="h-4 w-4 text-primary" /> {t.name}
            </div>
            <p className="mt-1 text-[12px] text-muted-foreground">{t.desc}</p>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span>{t.thread_count} thread{t.thread_count === 1 ? '' : 's'}</span>
              <span>·</span>
              <span>{t.post_count} post{t.post_count === 1 ? '' : 's'}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ThreadList({ topicSlug, onPick, onBack }) {
  const [data, setData] = useState({ topic: null, threads: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    forumApi.threads(topicSlug)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load threads.'))
      .finally(() => setLoading(false));
  }, [topicSlug]);
  useEffect(() => { reload(); }, [reload]);

  const submit = async () => {
    if (title.trim().length < 4 || body.trim().length < 4) {
      toast.error('Title needs at least 4 characters, body at least 4.');
      return;
    }
    setBusy(true);
    try {
      const r = await forumApi.createThread(topicSlug, title, body);
      toast.success('Thread created.');
      setTitle(''); setBody(''); setShowNew(false);
      onPick(r.thread.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create thread.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading threads…</div>;
  }
  return (
    <div className="space-y-3" data-testid="forum-thread-list">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={onBack} className="gap-1.5" data-testid="forum-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All topics
        </Button>
        <Button size="sm" onClick={() => setShowNew(v => !v)} className="gap-1.5" data-testid="forum-new-thread-btn">
          <Plus className="h-3.5 w-3.5" /> New thread
        </Button>
      </div>
      <div>
        <div className="text-[16px] font-semibold">{data.topic?.name}</div>
        <p className="text-[12px] text-muted-foreground">{data.topic?.desc}</p>
      </div>
      {showNew && (
        <div className="rounded-md border border-border bg-card p-3 shadow-panel space-y-2" data-testid="forum-new-thread-form">
          <Input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="Thread title" maxLength={160}
                  data-testid="forum-new-thread-title" />
          <Textarea value={body} onChange={e => setBody(e.target.value)}
                     rows={4} maxLength={10000} placeholder="What's on your mind?"
                     data-testid="forum-new-thread-body" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5"
                     data-testid="forum-new-thread-submit">
              <Send className="h-3.5 w-3.5" /> Post thread
            </Button>
          </div>
        </div>
      )}
      {data.threads.length === 0 && !showNew && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
          No threads yet. Be the first!
        </div>
      )}
      <div className="space-y-2">
        {data.threads.map((t) => (
          <button key={t.id} type="button"
                   onClick={() => onPick(t.id)}
                   data-testid={`forum-thread-${t.id}`}
                   className="block w-full text-left rounded-md border border-border bg-card p-3 shadow-panel hover:border-primary">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[13px] font-semibold flex items-center gap-1.5">
                {t.is_pinned && <Pin className="h-3 w-3 text-amber-700" />}
                {t.is_locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                {t.title}
              </div>
              <div className="text-[11px] text-muted-foreground whitespace-nowrap">{relTime(t.last_activity_at)}</div>
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              by <b className="text-foreground">{t.author_name}</b> · {t.post_count} post{t.post_count === 1 ? '' : 's'}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ThreadView({ threadId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState({ thread: null, posts: [] });
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    forumApi.thread(threadId)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load thread.'))
      .finally(() => setLoading(false));
  }, [threadId]);
  useEffect(() => { reload(); }, [reload]);

  const submitReply = async () => {
    if (replyBody.trim().length < 2) { toast.error('Reply too short.'); return; }
    setBusy(true);
    try {
      await forumApi.reply(threadId, replyBody);
      setReplyBody('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reply.');
    } finally { setBusy(false); }
  };

  const deletePost = async (pid) => {
    // Guard: actually invoke the confirm dialog and gate on the user's
    // choice. Previously this read `if (!window.confirm) return;` which
    // only checked function existence and never actually asked.
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await forumApi.deletePost(pid);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed.'); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading thread…</div>;
  }
  return (
    <div className="space-y-3" data-testid="forum-thread-view">
      <Button size="sm" variant="ghost" onClick={onBack} className="gap-1.5">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Button>
      <div>
        <div className="flex items-center gap-2">
          {data.thread?.is_pinned && <Badge className="bg-amber-100 text-amber-900 border border-amber-300 gap-1"><Pin className="h-3 w-3" /> Pinned</Badge>}
          {data.thread?.is_locked && <Badge className="bg-zinc-200 text-zinc-700 border border-zinc-300 gap-1"><Lock className="h-3 w-3" /> Locked</Badge>}
        </div>
        <h2 className="mt-1 text-[20px] font-semibold tracking-tight">{data.thread?.title}</h2>
        <div className="text-[11px] text-muted-foreground">in {data.thread?.topic_slug} · started by {data.thread?.author_name} · {relTime(data.thread?.created_at)}</div>
      </div>
      <div className="space-y-2">
        {data.posts.map((p) => (
          <div key={p.id} className="rounded-md border border-border bg-card p-3 shadow-panel" data-testid={`forum-post-${p.id}`}>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <div><b className="text-foreground">{p.author_name}</b> · {relTime(p.created_at)}{p.edited_at && <span> · edited {relTime(p.edited_at)}</span>}</div>
              {p.author_user_id === user?.user_id && !p.is_op && (
                <button type="button" onClick={() => deletePost(p.id)}
                         className="inline-flex items-center gap-1 text-destructive hover:underline"
                         data-testid={`forum-delete-post-${p.id}`}>
                  <Trash2 className="h-3 w-3" /> delete
                </button>
              )}
            </div>
            <div className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed">{p.body}</div>
          </div>
        ))}
      </div>
      {data.thread?.is_locked ? (
        <div className="rounded-md border border-border bg-panel-header/40 px-3 py-2 text-[12px] text-muted-foreground">
          This thread is locked. No new replies are accepted.
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card p-3 shadow-panel space-y-2" data-testid="forum-reply-form">
          <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={3}
                     placeholder="Reply…" data-testid="forum-reply-body" />
          <div className="flex justify-end">
            <Button size="sm" onClick={submitReply} disabled={busy} className="gap-1.5"
                     data-testid="forum-reply-submit">
              <Send className="h-3.5 w-3.5" /> Reply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommunityPage() {
  // View kind + current forum scope.
  const [scope, setScope] = useState('community');
  const [view, setView] = useState({ kind: 'topics' });
  const [showNewFromRoot, setShowNewFromRoot] = useState(false);
  const [rootTopics, setRootTopics] = useState({ topics: [], my_scopes: ['community'] });
  const goTopics = useCallback(() => setView({ kind: 'topics' }), []);

  // Cache the topic list at the CommunityPage level so the composer's
  // topic-picker doesn't have to re-fetch, and so we can offer the full
  // list of accessible topics as post targets.
  useEffect(() => {
    forumApi.topics()
      .then((d) => setRootTopics({
        topics:    d.topics || [],
        my_scopes: d.my_scopes || ['community'],
      }))
      .catch(() => {/* silent — inner list will retry */});
  }, []);
  const accessibleTopics = useMemo(
    () => rootTopics.topics.filter((t) => rootTopics.my_scopes.includes(t.scope || 'community')),
    [rootTopics],
  );
  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-testid="community-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">The Loom</div>
          <h1 className="text-[26px] font-semibold tracking-tight">Community</h1>
          <p className="mt-1 text-[12.5px] text-muted-foreground">A place for writers to support one another. Share work-in-progress, ask for feedback, and learn from peers.</p>
        </div>
        {view.kind === 'topics' && accessibleTopics.length > 0 && (
          <Button
            size="sm"
            onClick={() => setShowNewFromRoot((v) => !v)}
            className="shrink-0 gap-1.5"
            data-testid="forum-start-new-thread-root"
          >
            <Plus className="h-3.5 w-3.5" /> Start a new thread
          </Button>
        )}
      </div>
      {/* Global composer — pick a topic + title + body without drilling in. */}
      {view.kind === 'topics' && showNewFromRoot && (
        <NewThreadRootComposer
          topics={accessibleTopics}
          initialScope={scope}
          onClose={() => setShowNewFromRoot(false)}
          onCreated={(tid, topicSlug) => {
            setShowNewFromRoot(false);
            setView({ kind: 'thread', tid, lastSlug: topicSlug });
          }}
        />
      )}
      {/* Scope switcher — visible only on the topic-list view. */}
      {view.kind === 'topics' && (
        <div className="mb-4 inline-flex rounded-md border border-border bg-panel-header/50 p-0.5" data-testid="forum-scope-tabs">
          {Object.entries(SCOPE_META).map(([k, meta]) => {
            const Icon = meta.icon;
            const active = scope === k;
            return (
              <button key={k} type="button"
                       onClick={() => setScope(k)}
                       data-testid={`forum-scope-tab-${k}`}
                       className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] transition ${active ? 'bg-background text-foreground shadow-panel' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon className="h-3.5 w-3.5" /> {meta.label}
              </button>
            );
          })}
        </div>
      )}
      {view.kind === 'topics' && (
        <>
          <p className="mb-3 text-[11.5px] text-muted-foreground">{SCOPE_META[scope]?.blurb}</p>
          <TopicList scope={scope} onPick={(slug) => setView({ kind: 'topic', slug })} />
        </>
      )}
      {view.kind === 'topic' && (
        <ThreadList topicSlug={view.slug}
                     onPick={(tid) => setView({ kind: 'thread', tid, lastSlug: view.slug })}
                     onBack={goTopics} />
      )}
      {view.kind === 'thread' && (
        <ThreadView threadId={view.tid}
                     onBack={() => setView({ kind: 'topic', slug: view.lastSlug || '' })} />
      )}
    </div>
  );
}

/* -------------- Root-level composer (topic picker + title + body) --------------
 *
 * Renders directly on /community so a user never has to drill into a
 * topic first to start a thread. The topic dropdown is filtered to the
 * user's accessible scopes so basic-tier users don't see pro_house
 * options they can't post into.
 */
function NewThreadRootComposer({ topics, initialScope, onClose, onCreated }) {
  const [topicSlug, setTopicSlug] = useState('');
  const [title,     setTitle]     = useState('');
  const [body,      setBody]      = useState('');
  const [busy,      setBusy]      = useState(false);

  // Default the topic dropdown to the first topic in the currently
  // selected scope, otherwise the first accessible topic overall.
  useEffect(() => {
    if (topicSlug) return;
    const preferred = topics.find((t) => (t.scope || 'community') === initialScope);
    setTopicSlug((preferred || topics[0])?.slug || '');
  }, [topics, initialScope, topicSlug]);

  const submit = async () => {
    if (!topicSlug)      { toast.error('Pick a topic.'); return; }
    if (title.trim().length < 3) { toast.error('Title too short.'); return; }
    if (body.trim().length < 2)  { toast.error('Body too short.');  return; }
    setBusy(true);
    try {
      const r = await forumApi.createThread(topicSlug, title, body);
      toast.success('Thread posted.');
      onCreated(r.thread.id, topicSlug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-md border border-border bg-card p-3 shadow-panel space-y-2"
         data-testid="forum-new-thread-root-form">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[220px_1fr]">
        <div>
          <label htmlFor="forum-root-topic" className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Topic</label>
          <select id="forum-root-topic"
                   value={topicSlug}
                   onChange={(e) => setTopicSlug(e.target.value)}
                   data-testid="forum-new-thread-root-topic"
                   className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-[13px]">
            {topics.map((t) => (
              <option key={t.slug} value={t.slug}>
                {(t.scope === 'pro_house' ? '👑 ' : '')}
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="forum-root-title" className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Title</label>
          <Input id="forum-root-title" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="A clear headline for your thread"
                  maxLength={160}
                  className="mt-1"
                  data-testid="forum-new-thread-root-title" />
        </div>
      </div>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={5}
        maxLength={10000}
        placeholder="What's on your mind? (Tip: type @name to mention another writer.)"
        data-testid="forum-new-thread-root-body"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5"
                 data-testid="forum-new-thread-root-submit">
          <Send className="h-3.5 w-3.5" /> Post thread
        </Button>
      </div>
    </div>
  );
}
